import {
  findEntityIndex,
  getYucollectEntity,
  isSpoctoDomain,
} from 'features/Yucollect/Login/function';
import { isAgency, isAllocator } from 'features/Yucollect/utils/util';
import { setKfuseMetrics } from 'monitoring/utils';
import { put, select, takeLatest } from 'redux-saga/effects';
import { setUserConfiguration } from 'redux/slice/user/UserConfiguration.slice';

import {
  fetchUserEntities,
  fetchUserEntitiesFailed,
  fetchUserEntitiesSucceeded,
} from '../../../redux/slice/user/UserEntities.slice';
import { ApiStatusCodes } from '../../../services/api/apiStatusCode';
import { SPOCTO_ID, userEndpoints } from '../../../services/api/endPoints';
import { HTTPClient } from '../../../services/api/http-client';
import { RootState } from '../../store/store';
import { IFetchUserEntitiesResponse, IUserDataProps } from './User.types';

function getUserEntitiesWithLenderGroup(userData: IUserDataProps) {
  const ENTITY_GROUPS = ['allocator', 'agency', 'yucollect_ops'];
  const userEntitiesWithLenderGroup = userData?.user_entities?.filter(
    (entity) => {
      return entity?.groups?.some((group) =>
        ENTITY_GROUPS.includes(group?.group_code)
      );
    }
  );

  return userEntitiesWithLenderGroup;
}
export function* fetchUserEntitiesSaga(): Generator {
  try {
    const result: IFetchUserEntitiesResponse = yield HTTPClient.GetRequest(
      userEndpoints.GET_USER_ENTITIES
    );

    if (result?.status === ApiStatusCodes.SUCCESS && result?.data?.user) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { name, email, ca_user_id, default_entity, user_entities } =
        result?.data?.user;
      let entityId = default_entity;
      // Code for users with multiple entities.
      if (user_entities && user_entities?.length > 0) {
        let yucollectEntity = getYucollectEntity(user_entities);

        // if user is spocto gmail holder then update default values else consider normal values
        if (isSpoctoDomain(email)) {
          yucollectEntity = findEntityIndex(user_entities, SPOCTO_ID);
        }

        if (yucollectEntity !== -1) {
          entityId = isSpoctoDomain(email)
            ? SPOCTO_ID
            : user_entities[yucollectEntity].ca_entity_id;
          const entityName = user_entities[yucollectEntity].company_name;
          const userRole =
            user_entities[yucollectEntity]?.groups[0]?.roles[0]?.name;
          const userGroup =
            user_entities[yucollectEntity]?.groups[0]?.group_code;

          // Set agency/allocator specific IDs based on user group
          let agencyId = '';
          let agencyName = '';
          let lenderId = '';
          let lenderName = '';
          let lenderLogo = '';

          try {
            // Get current user configuration to preserve existing allocator selection
            const currentUserConfig = yield select(
              (state: RootState) => state.userConfiguration
            );

            if (isAgency(userGroup)) {
              agencyId = user_entities[yucollectEntity].ca_entity_id || '';
              agencyName = user_entities[yucollectEntity].company_name || '';

              // Preserve existing allocator selection for agency users
              lenderId = currentUserConfig?.lenderId || '';
              lenderName = currentUserConfig?.lenderName || '';
              lenderLogo = currentUserConfig?.lenderLogo || '';
            } else if (isAllocator(userGroup)) {
              lenderId = user_entities[yucollectEntity].ca_entity_id || '';
              lenderName = user_entities[yucollectEntity].company_name || '';

              // Preserve existing agency selection for allocator users
              agencyId = currentUserConfig?.agencyId || '';
              agencyName = currentUserConfig?.agencyName || '';
            }
          } catch (error) {
            console.error('[Saga] Error accessing current user config:', error);

            // Fallback: Set IDs without preserving existing selection
            if (isAgency(userGroup)) {
              agencyId = user_entities[yucollectEntity].ca_entity_id || '';
              agencyName = user_entities[yucollectEntity].company_name || '';
            } else if (isAllocator(userGroup)) {
              lenderId = user_entities[yucollectEntity].ca_entity_id || '';
              lenderName = user_entities[yucollectEntity].company_name || '';
            }
          }

          yield put(
            setUserConfiguration({
              entityUrl: undefined,
              entityId,
              entityName,
              userGroup,
              userRole,
              caUserName: name,
              caUserId: ca_user_id,
              agencyId,
              agencyName,
              lenderId,
              lenderName,
              lenderLogo,
            })
          );

          // Set user metrics for Kfuse monitoring
          setKfuseMetrics({
            id: ca_user_id,
            name,
            email,
            entityId,
            entityName,
            userGroup,
            userRole,
            agencyId,
            agencyName,
            lenderId,
            lenderName,
          });
        } else {
          console.error(
            '[Saga Error] No Yucollect entity found (yucollectEntity === -1)'
          );
        }
      }

      // For now we are processing only lender entities
      yield put(
        fetchUserEntitiesSucceeded({
          name,
          email,
          ca_user_id,
          default_entity: entityId,
          user_entities: getUserEntitiesWithLenderGroup(result?.data?.user),
        })
      );
    } else {
      yield put(
        fetchUserEntitiesFailed({
          error: {
            message: 'An error occurred while fetching user entities.',
          },
        })
      );
    }
  } catch (error) {
    // Handle API call failure or network errors
    yield put(
      fetchUserEntitiesFailed({
        error: {
          message: 'An error occurred while fetching user entities.',
        },
      })
    );
  }
}

export function* watchUserEntitiesSaga() {
  yield takeLatest(fetchUserEntities.type, fetchUserEntitiesSaga);
}
